import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { RebalancingRecommendation } from '@/hooks/use-rebalancing-executor';
import { useWallet } from '@/contexts/WalletContext';

interface RebalancingConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  recommendations: RebalancingRecommendation[];
  isExecuting?: boolean;
}

export default function RebalancingConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  recommendations,
  isExecuting = false,
}: RebalancingConfirmDialogProps) {
  const { balance } = useWallet();
  const [isConfirmed, setIsConfirmed] = useState(false);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const buys = recommendations.filter(r => r.action === 'Buy');
    const sells = recommendations.filter(r => r.action === 'Sell');

    const totalSolRequired = buys.reduce((sum, r) => sum + r.amount, 0);
    const estimatedFees = recommendations.length * 0.000005; // Rough estimate per transaction
    const estimatedSlippage = totalSolRequired * 0.005; // 0.5% slippage

    return {
      buysCount: buys.length,
      sellsCount: sells.length,
      totalSolRequired,
      estimatedFees,
      estimatedSlippage,
      totalCost: totalSolRequired + estimatedFees + estimatedSlippage,
    };
  }, [recommendations]);

  const isInsufficientBalance = balance < summary.totalSolRequired;

  const handleConfirm = () => {
    if (isConfirmed && !isInsufficientBalance) {
      onConfirm();
    }
  };

  const handleClose = () => {
    setIsConfirmed(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-modal="true" role="dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Confirm Rebalancing Execution
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-success">{summary.buysCount}</div>
              <div className="text-sm text-muted-foreground">Buys</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold text-destructive">{summary.sellsCount}</div>
              <div className="text-sm text-muted-foreground">Sells</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{summary.totalSolRequired.toFixed(4)}</div>
              <div className="text-sm text-muted-foreground">SOL Required</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{summary.totalCost.toFixed(4)}</div>
              <div className="text-sm text-muted-foreground">Total Cost</div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="space-y-2">
            <h4 className="font-medium">Cost Breakdown</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>SOL Amount:</span>
                <span>{summary.totalSolRequired.toFixed(4)} SOL</span>
              </div>
              <div className="flex justify-between">
                <span>Estimated Fees:</span>
                <span>{summary.estimatedFees.toFixed(6)} SOL</span>
              </div>
              <div className="flex justify-between">
                <span>Estimated Slippage:</span>
                <span>{summary.estimatedSlippage.toFixed(6)} SOL</span>
              </div>
              <Separator />
              <div className="flex justify-between font-medium">
                <span>Total:</span>
                <span>{summary.totalCost.toFixed(4)} SOL</span>
              </div>
            </div>
          </div>

          {/* Insufficient Balance Warning */}
          {isInsufficientBalance && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Insufficient SOL balance. You need {summary.totalSolRequired.toFixed(4)} SOL but only have {balance.toFixed(4)} SOL.
              </AlertDescription>
            </Alert>
          )}

          {/* Trade List */}
          <div>
            <h4 className="font-medium mb-3">Trades to Execute</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {recommendations.map((rec, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    {rec.action === 'Buy' ? (
                      <TrendingUp className="w-4 h-4 text-success" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-destructive" />
                    )}
                    <div>
                      <div className="font-medium">{rec.token}</div>
                      <div className="text-sm text-muted-foreground">
                        {rec.currentWeight.toFixed(1)}% → {rec.targetWeight.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={rec.action === 'Buy' ? 'default' : 'destructive'}>
                      {rec.action}
                    </Badge>
                    <div className="text-sm text-muted-foreground mt-1">
                      {rec.amount.toFixed(4)} {rec.action === 'Buy' ? 'SOL' : 'tokens'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Confirmation Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="confirm-rebalancing"
              checked={isConfirmed}
              onCheckedChange={(checked) => setIsConfirmed(checked as boolean)}
              disabled={isInsufficientBalance}
            />
            <label
              htmlFor="confirm-rebalancing"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I understand this will execute multiple transactions on the Solana blockchain
            </label>
          </div>

          {/* Warning */}
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This action cannot be undone. Please verify all trades before proceeding.
              Each transaction will require wallet confirmation.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isExecuting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isConfirmed || isInsufficientBalance || isExecuting}
          >
            {isExecuting ? 'Executing...' : 'Confirm & Execute'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}